import { useEffect } from 'react'
import {
  getStoredUserId,
  resolveInitialUserSelection,
  type InitialUserSelectionSource,
} from '@/utils'

type UpdateFormOptions = {
  runCallbacks?: boolean
  runValidation?: boolean
}

type ResolveResult<TValues> = {
  values: TValues | Partial<TValues>
  options?: UpdateFormOptions
}

type Params<TValues extends object> = {
  enabled: boolean
  routeUserId?: string
  routeUserLoading: boolean
  routeUserIsValid: boolean
  fallbackUserId: string
  loadStoredFormState: (skipTransforms?: boolean) => unknown | null
  resolveValues: (params: {
    storedState: Partial<TValues> | null
    initialUser: { userId: string; source: InitialUserSelectionSource }
  }) => ResolveResult<TValues>
  applyValues: (
    values: TValues | Partial<TValues>,
    options?: UpdateFormOptions
  ) => void
}

/**
 * Runs one-time initialization for user-based filters.
 *
 * It reads stored form state, resolves which user id should be used,
 * and applies the final values to the form.
 *
 * User id order is handled by `resolveInitialUserSelection`:
 * URL user id (if valid) -> stored user id -> fallback user id.
 */
export const useInitializeUserFilter = <TValues extends object>(
  params: Params<TValues>
) => {
  const {
    enabled,
    routeUserId,
    routeUserLoading,
    routeUserIsValid,
    fallbackUserId,
    loadStoredFormState,
    resolveValues,
    applyValues,
  } = params

  useEffect(() => {
    if (!enabled) return

    const storedState = loadStoredFormState(false) as Partial<TValues> | null
    const storedUserId = getStoredUserId(
      storedState as { user?: unknown } | null
    )
    const shouldWaitForRouteValidation = !!routeUserId && routeUserLoading

    if (shouldWaitForRouteValidation) return

    const initialUser = resolveInitialUserSelection({
      routeUserId,
      routeUserIsValid,
      storedUserId,
      fallbackUserId,
    })
    const { values, options } = resolveValues({ storedState, initialUser })

    applyValues(values, options)
  }, [
    applyValues,
    enabled,
    fallbackUserId,
    loadStoredFormState,
    resolveValues,
    routeUserId,
    routeUserIsValid,
    routeUserLoading,
  ])
}
